import { useEffect, useState } from 'react';
import { AlertCircle, ChevronLeft, ChevronRight, Loader2, RefreshCw } from 'lucide-react';
import { useAuth } from '@/features/auth';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import { fetchSalary, type SalaryResponse } from '../api';
import type { Language } from '@/types';

interface SalaryWorkspaceProps {
  language: Language;
}

const copy = {
  en: {
    title: 'Payroll',
    adminOnly: 'Only admins and supervisors can view payroll.',
    prev: 'Previous',
    next: 'Next',
    refresh: 'Refresh',
    colCleaner: 'Cleaner',
    colPoints: 'Points',
    colSalary: 'Salary',
    colStatus: 'Status',
    rateMissing: 'Rate missing',
    pending: 'unpaid',
    paid: 'paid',
    kpiCleaners: 'Cleaners',
    kpiPoints: 'Points',
    kpiTotal: 'Total salary',
    kpiPending: 'Unpaid',
    kpiPaid: 'Paid',
    emptyState: (month: string) => `No approved points recorded for ${month} yet.`,
    footer: 'Total',
  },
  sv: {
    title: 'Löneunderlag',
    adminOnly: 'Endast admin och städledare kan se löneunderlag.',
    prev: 'Föregående',
    next: 'Nästa',
    refresh: 'Uppdatera',
    colCleaner: 'Städare',
    colPoints: 'Poäng',
    colSalary: 'Lön',
    colStatus: 'Status',
    rateMissing: 'Taxa saknas',
    pending: 'ej utbetald',
    paid: 'utbetald',
    kpiCleaners: 'Städare',
    kpiPoints: 'Poäng',
    kpiTotal: 'Total lön',
    kpiPending: 'Ej utbetald',
    kpiPaid: 'Utbetald',
    emptyState: (month: string) => `Inga godkända poäng registrerade för ${month} ännu.`,
    footer: 'Totalt',
  },
} as const;

const MONTH_NAMES_SV = [
  'Januari', 'Februari', 'Mars', 'April', 'Maj', 'Juni',
  'Juli', 'Augusti', 'September', 'Oktober', 'November', 'December',
];
const MONTH_NAMES_EN = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

function currentYearMonth(): { year: number; month: number } {
  const now = new Date();
  return { year: now.getFullYear(), month: now.getMonth() + 1 };
}

function shift(year: number, month: number, delta: number): { year: number; month: number } {
  const d = new Date(year, month - 1 + delta, 1);
  return { year: d.getFullYear(), month: d.getMonth() + 1 };
}

function toDateRange(year: number, month: number): { from: string; to: string } {
  const from = `${year}-${String(month).padStart(2, '0')}-01`;
  const lastDay = new Date(year, month, 0).getDate();
  const to = `${year}-${String(month).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
  return { from, to };
}

function formatKr(value: number): string {
  return (
    new Intl.NumberFormat('sv-SE', { maximumFractionDigits: 0 }).format(value) + ' kr'
  );
}

export function SalaryWorkspace({ language }: SalaryWorkspaceProps) {
  const { session, profile } = useAuth();
  const text = copy[language];
  const token = session?.access_token ?? null;
  const canView = profile?.role === 'admin' || profile?.role === 'supervisor';

  const { year: initY, month: initM } = currentYearMonth();
  const [year, setYear] = useState(initY);
  const [month, setMonth] = useState(initM);
  const [data, setData] = useState<SalaryResponse | null>(null);
  const [loading, setLoading] = useState(true);

  const monthNames = language === 'sv' ? MONTH_NAMES_SV : MONTH_NAMES_EN;
  const monthLabel = monthNames[month - 1];

  const loadData = async () => {
    if (!token) return;
    setLoading(true);
    try {
      const { from, to } = toDateRange(year, month);
      const res = await fetchSalary(from, to, token);
      setData(res);
    } catch (err) {
      console.error('Failed to load salary data', err);
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (canView) void loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, year, month, canView]);

  if (!canView) {
    return (
      <div className="flex items-center justify-center py-16">
        <Card className="max-w-md">
          <CardContent className="py-8 text-center">
            <AlertCircle className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
            <p className="text-sm text-muted-foreground">{text.adminOnly}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const rows = data?.rows ?? [];
  const totals = data?.totals;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">{text.title}</h1>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex items-center rounded-md border bg-background">
            <Button
              variant="ghost"
              size="icon"
              className="h-9 w-9 rounded-none rounded-l-md"
              onClick={() => {
                const n = shift(year, month, -1);
                setYear(n.year);
                setMonth(n.month);
              }}
              aria-label={text.prev}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <div className="min-w-[140px] text-center text-sm font-semibold px-2">
              {monthLabel} {year}
            </div>
            <Button
              variant="ghost"
              size="icon"
              className="h-9 w-9 rounded-none rounded-r-md"
              onClick={() => {
                const n = shift(year, month, 1);
                setYear(n.year);
                setMonth(n.month);
              }}
              aria-label={text.next}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
          <Button variant="outline" size="sm" onClick={loadData} disabled={loading}>
            {loading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4" />
            )}
            <span className="ml-1.5">{text.refresh}</span>
          </Button>
        </div>
      </div>

      {/* KPI tiles */}
      {totals && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <KpiTile label={text.kpiCleaners} value={String(totals.cleaners)} />
          <KpiTile label={text.kpiPoints} value={String(totals.points)} />
          <KpiTile label={text.kpiTotal} value={formatKr(totals.kr)} emphasize />
          <KpiTile label={text.kpiPending} value={formatKr(totals.pending_kr)} tone="warning" />
          <KpiTile label={text.kpiPaid} value={formatKr(totals.paid_kr)} tone="success" />
        </div>
      )}

      {/* Content */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      ) : rows.length === 0 ? (
        <Card>
          <CardContent className="py-10 text-center text-sm text-muted-foreground">
            {text.emptyState(`${monthLabel} ${year}`)}
          </CardContent>
        </Card>
      ) : (
        <Card className="overflow-hidden">
          {/* Column headers */}
          <div className="hidden md:grid grid-cols-[1fr_100px_160px_1fr] items-center gap-3 px-4 py-2 border-b bg-muted/40 text-[11px] uppercase tracking-wide text-muted-foreground font-medium">
            <div>{text.colCleaner}</div>
            <div className="text-right">{text.colPoints}</div>
            <div className="text-right">{text.colSalary}</div>
            <div>{text.colStatus}</div>
          </div>

          {/* Rows */}
          <div className="divide-y">
            {rows.map((row) => {
              const name = row.cleaner_name ?? 'Okänd';
              const missingRate = row.kr_per_point === null;

              return (
                <div key={row.user_id} className="grid md:grid-cols-[1fr_100px_160px_1fr] gap-3 items-center px-4 py-3">
                  {/* Name */}
                  <div className="font-medium truncate">{name}</div>

                  {/* Points */}
                  <div className="text-right text-sm tabular-nums text-muted-foreground">
                    {row.points}
                  </div>

                  {/* Salary */}
                  <div className={cn('text-right text-sm font-semibold tabular-nums', missingRate && 'text-muted-foreground italic')}>
                    {missingRate ? text.rateMissing : formatKr(row.kr)}
                  </div>

                  {/* Status split */}
                  <div className="flex flex-wrap gap-2 text-xs">
                    {row.paid_kr > 0 && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-chart-4/15 text-chart-4 font-medium tabular-nums">
                        {formatKr(row.paid_kr)} {text.paid}
                      </span>
                    )}
                    {row.pending_kr > 0 && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-accent/50 text-accent-foreground font-medium tabular-nums">
                        {formatKr(row.pending_kr)} {text.pending}
                      </span>
                    )}
                    {row.paid_kr === 0 && row.pending_kr === 0 && !missingRate && (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Footer totals */}
          {totals && (
            <div className="grid md:grid-cols-[1fr_100px_160px_1fr] gap-3 items-center px-4 py-3 border-t bg-muted/40 font-semibold text-sm">
              <div>{text.footer}</div>
              <div className="text-right tabular-nums">{totals.points}</div>
              <div className="text-right tabular-nums">{formatKr(totals.kr)}</div>
              <div className="flex flex-wrap gap-2 text-xs">
                {totals.paid_kr > 0 && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-chart-4/15 text-chart-4 font-medium tabular-nums">
                    {formatKr(totals.paid_kr)} {text.paid}
                  </span>
                )}
                {totals.pending_kr > 0 && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-accent/50 text-accent-foreground font-medium tabular-nums">
                    {formatKr(totals.pending_kr)} {text.pending}
                  </span>
                )}
              </div>
            </div>
          )}
        </Card>
      )}
    </div>
  );
}

interface KpiTileProps {
  label: string;
  value: string;
  tone?: 'warning' | 'success';
  emphasize?: boolean;
}

function KpiTile({ label, value, tone, emphasize }: KpiTileProps) {
  return (
    <Card>
      <CardContent className="p-4">
        <div className="text-[11px] uppercase tracking-wide text-muted-foreground font-medium">
          {label}
        </div>
        <div
          className={cn(
            'mt-1 text-2xl font-bold tabular-nums',
            tone === 'warning' && 'text-accent-foreground',
            tone === 'success' && 'text-chart-4',
            emphasize && 'text-foreground',
          )}
        >
          {value}
        </div>
      </CardContent>
    </Card>
  );
}
